import { ImageLoaderProps } from "next/image";

// Runs both server- and client-side (Next.js recomputes srcSet in the
// browser), so it must never build a signed URL itself — the signing
// key would leak into the client bundle. Instead it points at our own
// API route, which signs server-side and redirects to Thumbor.
// See src/pages/api/thumbor-sign.ts and src/utils/thumbor.ts.
//
// The URL returned here is what ends up in the HTML and therefore in Google's
// image index, so it deliberately carries no signature: it stays valid across
// THUMBOR_SECURITY_KEY rotations.

// Formats Thumbor cannot improve on (vector) or would degrade (animated GIF).
const PASSTHROUGH_EXTENSIONS = [".svg", ".gif"];

function isPassthroughSource(src: string): boolean {
  // Strip query/hash before checking the extension — WordPress appends cache
  // busters like "?ver=123" that would defeat a plain endsWith().
  const pathname = src.split(/[?#]/)[0].toLowerCase();
  return PASSTHROUGH_EXTENSIONS.some((ext) => pathname.endsWith(ext));
}

export default function thumborLoader({
  src,
  width,
  quality,
}: ImageLoaderProps) {
  // Local/static assets (public/, imported files) and data URIs are served by
  // Next itself — Thumbor cannot fetch them and the API route would 400 them.
  // Video posters in particular arrive as data URIs from Vimeo.
  if (!src.startsWith("http")) return src;
  if (isPassthroughSource(src)) return src;

  const params = new URLSearchParams({
    src,
    w: String(width),
    q: String(quality || 90),
  });
  // Trailing slash on purpose: next.config.js sets trailingSlash: true, so the
  // slash-less URL answers with a 308 to this one — an extra round trip in
  // front of every single image.
  return `/api/thumbor-sign/?${params.toString()}`;
}
